/*
 * Lady Pop · Cesta premium
 *  - <lp-countdown>: cuenta atrás hasta la fecha de fin REAL del descuento automático.
 *    Cuando la oferta termina, la barra de la oferta desaparece sola.
 *  - <lp-hold-timer>: contador de 10 min (siempre visible; vuelve a empezar al llegar a 0).
 *  - <lp-cart-recs>: carrusel «Completa tu look» con productos complementarios
 *    (recomendaciones de Shopify) y añadido a la cesta eligiendo la talla.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

/** Ancho de la barra: 75 % al empezar → 100 % al terminar */
function lpBarWidth(elapsed) {
  const e = Math.max(0, Math.min(1, elapsed || 0));
  return 75 + 25 * e;
}

/* ---------------------------------------------------------------- */
/* Cuenta atrás                                                      */
/* ---------------------------------------------------------------- */
class LpCountdown extends HTMLElement {
  #timer = null;
  #observer = null;
  #text = '';
  #writing = false;

  connectedCallback() {
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
    // La cesta se vuelve a pintar (morph) en cada cambio: si el texto se pisa, se repone al momento
    this.#observer = new MutationObserver(() => {
      if (this.#writing) return;
      if (this.textContent !== this.#text) this.#tick();
    });
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#observer?.disconnect();
  }

  #tick() {
    const end = Number(this.dataset.end) * 1000;
    const left = end - Date.now();
    const box = this.closest('.lp-offer');

    if (!end || left <= 0) {
      if (box) box.hidden = true;
      clearInterval(this.#timer);
      return;
    }

    // Siempre en horas, minutos y segundos (sin días)
    const s = Math.floor(left / 1000);
    const h = Math.floor(s / 3600);
    const m = Math.floor((s % 3600) / 60);
    const sec = s % 60;
    const pad = (n) => String(n).padStart(2, '0');
    const text = `${pad(h)}:${pad(m)}:${pad(sec)}`;

    // Barra: empieza rellena 3/4 y se completa hacia la derecha a medida que pasa el tiempo
    const fill = box && box.querySelector('.lp-offer__fill');
    const start = Number(this.dataset.start) * 1000;
    if (fill) {
      const total = start && end > start ? end - start : 24 * 3600 * 1000;
      fill.style.width = `${lpBarWidth(1 - left / total)}%`;
    }

    this.#text = text;
    if (this.textContent !== text) {
      this.#writing = true;
      this.textContent = text;
      this.#writing = false;
    }
  }
}

if (!customElements.get('lp-countdown')) customElements.define('lp-countdown', LpCountdown);

/* ---------------------------------------------------------------- */
/* Contador de 10 minutos («El precio de tus artículos se mantendrá…») */
/* Siempre visible: al llegar a 0 vuelve a empezar desde 10:00.     */
/* ---------------------------------------------------------------- */
class LpHoldTimer extends HTMLElement {
  static KEY = 'lp-hold-start';
  #timer = null;
  #observer = null;
  #text = '';
  #writing = false;

  connectedCallback() {
    this.#tick();
    this.#timer = setInterval(() => this.#tick(), 1000);
    this.#observer = new MutationObserver(() => {
      if (this.#writing) return;
      if (this.textContent !== this.#text) this.#tick();
    });
    this.#observer.observe(this, { childList: true, characterData: true, subtree: true });
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
    this.#observer?.disconnect();
  }

  #start() {
    let start = 0;
    try {
      start = Number(localStorage.getItem(LpHoldTimer.KEY)) || 0;
      if (!start) {
        start = Date.now();
        localStorage.setItem(LpHoldTimer.KEY, String(start));
      }
    } catch (e) {
      start = window.__lpHoldStart || (window.__lpHoldStart = Date.now());
    }
    return start;
  }

  #tick() {
    const total = (Number(this.dataset.minutes) || 10) * 60 * 1000;
    // Ciclos de 10 min: al llegar a 0 vuelve a empezar, así siempre está visible
    const elapsed = Math.max(0, Date.now() - this.#start()) % total;
    const left = total - elapsed;
    const box = this.closest('.lp-offer');
    if (box && box.hidden) box.hidden = false;

    const s = Math.ceil(left / 1000);
    const pad = (n) => String(n).padStart(2, '0');
    const text = `${pad(Math.floor(s / 60))}:${pad(s % 60)} min`;

    const fill = box && box.querySelector('.lp-offer__fill');
    if (fill) fill.style.width = `${lpBarWidth(1 - left / total)}%`;

    this.#text = text;
    if (this.textContent !== text) {
      this.#writing = true;
      this.textContent = text;
      this.#writing = false;
    }
  }
}

if (!customElements.get('lp-hold-timer')) customElements.define('lp-hold-timer', LpHoldTimer);

/* ---------------------------------------------------------------- */
/* Cesta lateral: sin botón «Eliminar» → la cantidad puede bajar a 0 */
/* (Horizon elimina la línea cuando la cantidad es 0)                */
/* ---------------------------------------------------------------- */
function lpAllowZeroQuantity() {
  document.querySelectorAll('#cart-drawer cart-quantity-selector-component').forEach((el) => {
    const input = el.querySelector('input[data-cart-line]');
    if (!input) return;
    if (input.getAttribute('min') !== '0') input.setAttribute('min', '0');
    try {
      el.updateButtonStates?.();
    } catch (e) {}
  });
}
let lpZeroQueued = false;
const lpZeroObserver = new MutationObserver(() => {
  if (lpZeroQueued) return;
  lpZeroQueued = true;
  queueMicrotask(() => {
    lpZeroQueued = false;
    lpAllowZeroQuantity();
  });
});
lpZeroObserver.observe(document.documentElement, {
  childList: true,
  subtree: true,
  attributes: true,
  attributeFilter: ['min'],
});
lpAllowZeroQuantity();

/* ---------------------------------------------------------------- */
/* Carrusel «Completa tu look»                                       */
/* ---------------------------------------------------------------- */
const SIZE_RE = /talla|size|tama/i;
const cache = new Map(); // product_id -> Promise<products[]>

function formatMoney(cents, format) {
  const fmt = (format || '{{amount_with_comma_separator}} €').replace(/<[^>]*>/g, '');
  const value = Number(cents) / 100;
  const group = (num, decimals, thousands, decimal) => {
    const [int, dec] = num.toFixed(decimals).split('.');
    const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return dec ? `${withThousands}${decimal}${dec}` : withThousands;
  };
  return fmt.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_no_decimals':
        return group(value, 0, ',', '.');
      case 'amount_with_comma_separator':
        return group(value, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return group(value, 0, '.', ',');
      case 'amount_with_apostrophe_separator':
        return group(value, 2, "'", '.');
      default:
        return group(value, 2, ',', '.');
    }
  });
}

function imageUrl(src, width) {
  if (!src) return '';
  const url = typeof src === 'string' ? src : src.src || src.url || '';
  if (!url) return '';
  return url + (url.includes('?') ? '&' : '?') + 'width=' + width;
}

function esc(str) {
  return String(str ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);
}

function optionNames(product) {
  return (product.options || []).map((o) => (typeof o === 'string' ? o : o.name));
}

class LpCartRecs extends HTMLElement {
  static observedAttributes = ['data-ids', 'data-sub'];

  #products = [];
  #html = '';
  #observer = null;
  #loadToken = 0;

  connectedCallback() {
    this.#observer = new MutationObserver(() => {
      // El morph de la cesta vacía este contenedor: se repone desde la caché sin volver a pedir nada
      if (this.#html && !this.querySelector('.lp-recs')) this.#paint();
    });
    this.#observer.observe(this, { childList: true });
    this.addEventListener('click', this.#onClick);
    this.#load();
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.removeEventListener('click', this.#onClick);
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === null || oldValue === newValue || !this.isConnected) return;
    if (name === 'data-sub') {
      // Cambia el % del siguiente tramo (p. ej. al subir la cantidad): se repinta sin volver a pedir productos
      if (this.#products.length) {
        this.#html = this.#render(this.#products);
        this.#paint();
      }
      return;
    }
    this.#load();
  }

  get #cartIds() {
    return (this.dataset.ids || '').split(',').filter(Boolean);
  }

  #fetchRecs(productId, intent) {
    const key = `${productId}:${intent}`;
    if (!cache.has(key)) {
      const url = `${this.dataset.url}.json?product_id=${productId}&limit=10&intent=${intent}`;
      cache.set(
        key,
        fetch(url)
          .then((r) => (r.ok ? r.json() : { products: [] }))
          .then((j) => j.products || [])
          .catch(() => [])
      );
    }
    return cache.get(key);
  }

  async #load() {
    const token = ++this.#loadToken;
    const ids = this.#cartIds;
    if (!ids.length || !this.dataset.url) return this.#clear();

    // Base: los dos últimos productos añadidos. Primero los complementarios, después los relacionados.
    const bases = ids.slice(0, 2);
    const lists = await Promise.all([
      ...bases.map((id) => this.#fetchRecs(id, 'complementary')),
      ...bases.map((id) => this.#fetchRecs(id, 'related')),
    ]);
    if (token !== this.#loadToken) return;

    const inCart = new Set(ids.map(String));
    const seen = new Set();
    const result = [];
    for (const list of lists) {
      for (const p of list) {
        const id = String(p.id);
        if (inCart.has(id) || seen.has(id) || !p.available) continue;
        seen.add(id);
        result.push(p);
        if (result.length >= 8) break;
      }
      if (result.length >= 8) break;
    }

    this.#products = result;
    if (!result.length) return this.#clear();
    this.#html = this.#render(result);
    this.#paint();
  }

  #clear() {
    this.#products = [];
    this.#html = '';
    this.replaceChildren();
  }

  #paint() {
    this.innerHTML = this.#html;
    this.#updateNav();
    this.querySelector('.lp-recs__track')?.addEventListener('scroll', () => this.#updateNav(), { passive: true });
    requestAnimationFrame(() => this.#updateNav());
  }

  /** Flechas: se desactivan al principio y al final; se ocultan si no hay nada que deslizar */
  #updateNav() {
    const track = this.querySelector('.lp-recs__track');
    if (!track) return;
    const prev = this.querySelector('[data-lp-prev]');
    const next = this.querySelector('[data-lp-next]');
    const max = track.scrollWidth - track.clientWidth - 2;
    if (prev) prev.disabled = track.scrollLeft <= 2;
    if (next) next.disabled = track.scrollLeft >= max;
    const nav = this.querySelector('.lp-recs__nav');
    if (nav && track.clientWidth) nav.hidden = max <= 0;
  }

  #render(products) {
    const fmt = this.dataset.moneyFormat;
    const sub = this.dataset.sub
      ? `<p class="lp-recs__sub">${esc(this.dataset.sub).replace(/(-\d+%)/, '<strong>$1</strong>')}</p>`
      : '';
    const chevron = (dir) =>
      `<svg viewBox="0 0 10 10" width="10" height="10" aria-hidden="true"><path d="${
        dir === 'prev' ? 'M6.5 1.5 3 5l3.5 3.5' : 'M3.5 1.5 7 5 3.5 8.5'
      }" fill="none" stroke="currentColor" stroke-width="1.4"/></svg>`;

    const cards = products
      .map((p, i) => {
        const v = p.variants?.find((x) => x.available) || p.variants?.[0];
        const price = v?.price ?? p.price;
        const compare = v?.compare_at_price ?? p.compare_at_price;
        const onSale = compare && compare > price;
        const pct = onSale ? Math.round((1 - price / compare) * 100) : 0;
        const img = p.featured_image || p.images?.[0];
        const priceHtml = onSale
          ? `<span class="lp-rec__sale">${esc(formatMoney(price, fmt))}<span>-${pct}%</span></span><s class="lp-rec__compare">${esc(formatMoney(compare, fmt))}</s>`
          : `<span>${esc(formatMoney(price, fmt))}</span>`;

        return `
        <article class="lp-rec" data-index="${i}">
          <div class="lp-rec__media">
            <a href="${esc(p.url)}" class="lp-rec__img-link" tabindex="-1" aria-hidden="true">
              ${img ? `<img src="${esc(imageUrl(img, 360))}" srcset="${esc(imageUrl(img, 240))} 240w, ${esc(imageUrl(img, 360))} 360w, ${esc(imageUrl(img, 480))} 480w" sizes="150px" alt="${esc(p.title)}" loading="lazy" width="360" height="480">` : ''}
            </a>
            <button type="button" class="lp-rec__add" data-lp-open aria-label="Añadir ${esc(p.title)} a la cesta">
              <svg viewBox="0 0 12 12" width="12" height="12" aria-hidden="true"><path d="M6 1v10M1 6h10" stroke="currentColor" stroke-width="1.2" fill="none"/></svg>
            </button>
            <div class="lp-rec__sizes" hidden></div>
          </div>
          <a href="${esc(p.url)}" class="lp-rec__title">${esc(p.title)}</a>
          <p class="lp-rec__price">${priceHtml}</p>
        </article>`;
      })
      .join('');

    return `
      <div class="lp-recs">
        <div class="lp-recs__head">
          <div>
            <p class="lp-recs__title">${esc(this.dataset.heading || 'Completa tu look')}</p>
            ${sub}
          </div>
          <div class="lp-recs__nav">
            <button type="button" data-lp-prev aria-label="Anterior">${chevron('prev')}</button>
            <button type="button" data-lp-next aria-label="Siguiente">${chevron('next')}</button>
          </div>
        </div>
        <div class="lp-recs__track" role="list">${cards}</div>
      </div>`;
  }

  /** Opciones de talla del producto (manteniendo el resto de opciones fijas) */
  #sizeChoices(product) {
    const variants = product.variants || [];
    if (variants.length <= 1) return null;
    const names = optionNames(product);
    const sizeIdx = names.findIndex((n) => SIZE_RE.test(n));
    const base = variants.find((v) => v.available) || variants[0];

    if (sizeIdx === -1) {
      return variants.map((v) => ({ id: v.id, label: v.title, available: v.available }));
    }

    const others = (v) => (v.options || []).every((val, i) => i === sizeIdx || val === base.options[i]);
    return variants.filter(others).map((v) => ({ id: v.id, label: v.options[sizeIdx], available: v.available }));
  }

  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const prev = target.closest('[data-lp-prev]');
    const next = target.closest('[data-lp-next]');
    if (prev || next) {
      const track = this.querySelector('.lp-recs__track');
      const card = track?.querySelector('.lp-rec');
      if (track && card) {
        const step = card.getBoundingClientRect().width + 12;
        track.scrollBy({ left: prev ? -step : step, behavior: 'smooth' });
      }
      return;
    }

    const open = target.closest('[data-lp-open]');
    if (open) {
      const card = open.closest('.lp-rec');
      const product = this.#products[Number(card?.dataset.index)];
      if (!card || !product) return;
      const choices = this.#sizeChoices(product);
      if (!choices) {
        const v = product.variants?.find((x) => x.available) || product.variants?.[0];
        if (v) this.#add(card, v.id);
        return;
      }
      this.#openSizes(card, product, choices);
      return;
    }

    const close = target.closest('[data-lp-close]');
    if (close) {
      this.#closeSizes(close.closest('.lp-rec'));
      return;
    }

    const size = target.closest('[data-lp-variant]');
    if (size && !size.disabled) {
      this.#add(size.closest('.lp-rec'), size.dataset.lpVariant);
    }
  };

  #openSizes(card, product, choices) {
    this.querySelectorAll('.lp-rec__sizes:not([hidden])').forEach((el) => this.#closeSizes(el.closest('.lp-rec')));
    const panel = card.querySelector('.lp-rec__sizes');
    panel.innerHTML = `
      <div class="lp-rec__sizes-head">
        <span>Elige tu talla</span>
        <button type="button" data-lp-close aria-label="Cerrar">
          <svg viewBox="0 0 10 10" width="9" height="9" aria-hidden="true"><path d="M1 1l8 8M9 1 1 9" stroke="currentColor" stroke-width="1.2"/></svg>
        </button>
      </div>
      <div class="lp-rec__chips">
        ${choices
          .map(
            (c) =>
              `<button type="button" class="lp-rec__chip" data-lp-variant="${c.id}" ${c.available ? '' : 'disabled aria-disabled="true"'}>${esc(c.label)}</button>`
          )
          .join('')}
      </div>`;
    panel.hidden = false;
    card.classList.add('is-open');
    card.querySelector('.lp-rec__add')?.setAttribute('hidden', '');
    panel.querySelector('.lp-rec__chip:not([disabled])')?.focus({ preventScroll: true });
  }

  #closeSizes(card) {
    if (!card) return;
    const panel = card.querySelector('.lp-rec__sizes');
    if (panel) {
      panel.hidden = true;
      panel.innerHTML = '';
    }
    card.classList.remove('is-open');
    card.querySelector('.lp-rec__add')?.removeAttribute('hidden');
  }

  async #add(card, variantId) {
    if (!card || card.classList.contains('is-adding')) return;
    card.classList.add('is-adding');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => /** @type {HTMLElement} */ (el).dataset.sectionId)
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = window.Theme?.routes || {};
    try {
      const res = await fetch(routes.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      const cart = await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } }).then((r) =>
        r.json()
      );

      if (data.status) {
        // Error (por ejemplo, sin stock)
        const panel = card.querySelector('.lp-rec__sizes-head span');
        if (panel) panel.textContent = data.description || data.message || 'No disponible';
        card.classList.remove('is-adding');
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, itemCount: 0, source: 'lp-cart-recs', didError: true },
        });
        return;
      }

      card.classList.remove('is-adding');
      card.classList.add('is-added');
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: 1,
          source: 'lp-cart-recs',
          sections: data.sections,
          didError: false,
        },
      });
    } catch (error) {
      card.classList.remove('is-adding');
      deferred.reject(error);
      console.error(error);
    }
  }
}

if (!customElements.get('lp-cart-recs')) customElements.define('lp-cart-recs', LpCartRecs);
